import React, { useRef, useEffect, useState, useCallback } from 'react';

const VideoPlayer = ({
  src,
  onTimeUpdate,
  onDraw,
  canvasRef,
  videoRef, // Receive videoRef as a prop
  rectangles,
  currentTime,
  isEditingEnabled,
  updateRectangle,
  updateScore,
  timeRange,
  scores,
  inputScore,
  fps, // Receive fps as a prop
  frameCount, // Receive frameCount as a prop
  width, // Receive width as a prop
  height, // Receive height as a prop
  newRectangle, // Receive newRectangle as a prop
  setNewRectangle, // Receive setNewRectangle as a prop
  isEditing,     
  setIsEditing,  
  onPlaybackError,
  crossOrigin = "anonymous",
}) => {
  const containerRef = useRef(null);
  const isDrawing = useRef(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  

  useEffect(() => {
    const handleLoadedMetadata = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas) {
        canvas.width = width;
        canvas.height = height;
      }
    };

    const video = videoRef.current;
    if (video) {
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
    }

    return () => {
      if (video) {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      }
    };
  }, [canvasRef, src]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [src]);

  const drawRectangles = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    rectangles.forEach((rect) => {
      const time = rect.frame / fps;
      if (currentTime >= time && currentTime < time + 1 / fps) {
        ctx.strokeStyle = 'red';
        ctx.lineWidth = 2;
        ctx.strokeRect(rect.x1, rect.y1, rect.x2 - rect.x1, rect.y2 - rect.y1);

        // Draw the score near the rectangle
        const score = scores.find((score) => score.frame === rect.frame);
        if (score && score.score !== undefined) {
          const text = `Score: ${score.score.toFixed(6)}`;
          ctx.font = '12px Arial';
          const textWidth = ctx.measureText(text).width;
          const textHeight = 12; // Approximate text height
          ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'; // Semi-transparent dark background
          ctx.fillRect(
            rect.x1,
            rect.y1 - textHeight - 5,
            textWidth + 4,
            textHeight + 4
          );
          ctx.fillStyle = 'white'; // White text color
          ctx.fillText(text, rect.x1 + 2, rect.y1 - 5);
        }
      }
    });
    if (newRectangle) {
      ctx.strokeStyle = 'blue';
      ctx.lineWidth = 2;
      ctx.strokeRect(
        newRectangle.x1,
        newRectangle.y1,
        newRectangle.x2 - newRectangle.x1,
        newRectangle.y2 - newRectangle.y1
      );
    }
  }, [currentTime, rectangles, newRectangle, scores, fps]);

  useEffect(() => {
    drawRectangles();
  }, [currentTime, rectangles, newRectangle, drawRectangles]);

  const handleMouseDown = (event) => {
    if (!isEditing) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;

    setStartPos({ x, y });
    setNewRectangle({ x1: x, y1: y, x2: x, y2: y });
    isDrawing.current = true;
  };

  const handleMouseMove = (event) => {
    if (!isEditing || !isDrawing.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;

    setNewRectangle((prevRect) => {
      const updatedRect = { ...prevRect, x2: x, y2: y };
      return updatedRect;
    });
  };

  const handleMouseUp = () => {
    if (!isEditing) return;
    isDrawing.current = false;
  };

  const handleSave = () => {
    if (newRectangle) {
      const startFrame = Math.floor(timeRange.start * fps);
      const endFrame = Math.floor(timeRange.end * fps);
      const updatedRectangles = rectangles.map((rect) => {
        if (rect.frame >= startFrame && rect.frame <= endFrame) {
          return { ...newRectangle, frame: rect.frame };
        }
        return rect;
      });
      for (let frame = startFrame; frame <= endFrame; frame++) {
        if (!updatedRectangles.some((rect) => rect.frame === frame)) {
          updatedRectangles.push({ ...newRectangle, frame });
        }
      }
      updateRectangle(updatedRectangles);
      setNewRectangle(null);
      inputScore = parseInt(inputScore);
      if (inputScore === 0 || inputScore === 1) {
        const updatedScores = scores.map((score) => {
          if (score.frame >= startFrame && score.frame <= endFrame) {
            return { score: inputScore, frame: score.frame };
          }
          return score;
        });

        for (let frame = startFrame; frame <= endFrame; frame++) {
          if (!updatedScores.some((score) => score.frame === frame)) {
            updatedScores.push({ score: inputScore, frame });
          }
        }
        updateScore(updatedScores);
      }
    }
  };

  const toggleEditing = () => {
    setIsEditing(!isEditing);
    setNewRectangle(null);
    if (canvasRef.current) {
      canvasRef.current.style.pointerEvents = isEditing ? 'none' : 'auto';
    }
  };

  const clearCanvas = () => {
    setNewRectangle(null); // Reset the newRectangle state
  };

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleTimeUpdate = () => {
      if (videoElement.currentTime > timeRange.end) {
        videoElement.currentTime = timeRange.start;
      }
      onTimeUpdate({ target: videoElement });
    };

    videoElement.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [timeRange, videoRef, onTimeUpdate]);

  return (
    <div>
      <div
        ref={containerRef}
        className='video-container'
        style={{ position: 'relative', display: 'inline-block' }}
      >
        <video
          ref={videoRef}
          src={src}
          controls
          onTimeUpdate={onTimeUpdate}
          onError={onPlaybackError}
          width={width}
          height={height}
          preload="auto"
          crossOrigin={crossOrigin}
        >
          <source src={src} type='video/mp4' />
          Your browser does not support the video tag.
        </video>
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            pointerEvents: isEditing ? 'auto' : 'none',
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        />
      </div>
      <div style={{ padding: '4px' }}>
        <button
          onClick={toggleEditing}
          style={{ marginRight: '10px' }}
          disabled={!isEditingEnabled}
        >
          {isEditing ? 'Stop Editing' : 'Edit'}
        </button>
        {/* <button
          onClick={handleSave}
          style={{ marginRight: '10px' }}
          disabled={!newRectangle}
        >
          Save
        </button> */}
        <button onClick={clearCanvas}>Clear</button>
      </div>
    </div>
  );
};

export default VideoPlayer;
